import type { IGetLatestPaymentInfoResponse } from '@/shared/api/billing/types';
import type { BeginCheckoutEventData } from '@/shared/thirdParty/gtm/types';
import type { User } from '@/shared/types/User';

import * as Sentry from '@sentry/react';

import { IS_WHITE_LABEL } from '@/shared/environments';

const MICROSOFT_ADS_SCRIPT_ID = 'microsoft-ads-uet';
const OPENAI_ADS_SCRIPT_ID = 'openai-ads-pixel';
const ADS_SESSION_EVENT_PREFIX = 'rask:ads:event';
const COOKIEBOT_CONSENT_EVENTS = ['CookiebotOnConsentReady', 'CookiebotOnAccept', 'CookiebotOnDecline'] as const;

const MICROSOFT_ADS_UET_ID = import.meta.env.VITE_MICROSOFT_ADS_UET_ID;
const OPENAI_ADS_PIXEL_ID = import.meta.env.VITE_OPENAI_ADS_PIXEL_ID;

const ADS_EVENT_NAMES = {
  beginCheckout: 'begin_checkout',
  purchase: 'purchase',
  signUp: 'sign_up',
  trialPreviewViewed: 'preview_view',
} as const;

type AdsEventName = (typeof ADS_EVENT_NAMES)[keyof typeof ADS_EVENT_NAMES];

const OPENAI_ADS_EVENTS: Record<
  AdsEventName,
  {
    eventName: 'checkout_started' | 'contents_viewed' | 'lead_created' | 'subscription_created';
    type: 'contents' | 'customer_action' | 'plan_enrollment';
  }
> = {
  [ADS_EVENT_NAMES.beginCheckout]: { eventName: 'checkout_started', type: 'contents' },
  [ADS_EVENT_NAMES.purchase]: { eventName: 'subscription_created', type: 'plan_enrollment' },
  [ADS_EVENT_NAMES.signUp]: { eventName: 'lead_created', type: 'customer_action' },
  [ADS_EVENT_NAMES.trialPreviewViewed]: { eventName: 'contents_viewed', type: 'contents' },
};

type AdsTrackingConfig = {
  enabled?: boolean;
  microsoftUetId?: string;
  openAiPixelId?: string;
};

type RevenueData = {
  currency: string;
  value: number;
};

type AdsUserData = {
  openAiUser: {
    country?: string;
    email_sha256: string;
    external_id_sha256: string;
  };
  microsoftEmailSha256: string;
};

let removeCookiebotConsentListeners: (() => void) | undefined;
let adsUserData: AdsUserData | undefined;
let adsUserGeneration = 0;
let microsoftSyncedUserHash: string | undefined;
let openAiSyncedUserHash: string | undefined;

const appendAsyncScript = ({ id, src, onLoad }: { id: string; src: string; onLoad?: () => void }) => {
  const script = document.createElement('script');

  script.async = true;
  script.id = id;
  script.src = src;
  if (onLoad) script.onload = onLoad;

  document.head.appendChild(script);
};

const initMicrosoftAds = (uetId: string) => {
  if (document.getElementById(MICROSOFT_ADS_SCRIPT_ID)) return;

  const queue = (window.uetq || []) as NonNullable<Window['uetq']>;
  window.uetq = queue;
  queue.push('consent', 'default', { ad_storage: 'denied' });

  appendAsyncScript({
    id: MICROSOFT_ADS_SCRIPT_ID,
    src: `https://bat.bing.com/bat.js?ti=${uetId}`,
    onLoad: () => {
      if (!window.UET) return;

      const queuedEvents = window.uetq;
      window.uetq = new window.UET({
        enableAutoSpaTracking: true,
        q: queuedEvents,
        ti: uetId,
        ts: Date.now(),
      });
      window.uetq.push('pageLoad');
    },
  });
};

const hasMarketingConsent = () => window.Cookiebot?.consent?.marketing === true;

const syncAdsUserData = () => {
  if (!adsUserData || !hasMarketingConsent()) return;

  if (window.uetq && microsoftSyncedUserHash !== adsUserData.microsoftEmailSha256) {
    window.uetq.push('set', {
      pid: {
        em: adsUserData.microsoftEmailSha256,
      },
    });
    microsoftSyncedUserHash = adsUserData.microsoftEmailSha256;
  }

  const openAiUserHash = adsUserData.openAiUser.external_id_sha256;

  if (window.oaiq && openAiSyncedUserHash !== openAiUserHash) {
    window.oaiq('init', { user: adsUserData.openAiUser });
    openAiSyncedUserHash = openAiUserHash;
  }
};

const updateMicrosoftAdsConsent = () => {
  window.uetq?.push('consent', 'update', {
    ad_storage: hasMarketingConsent() ? 'granted' : 'denied',
  });
};

const initOpenAiAds = (pixelId: string) => {
  if (document.getElementById(OPENAI_ADS_SCRIPT_ID)) return;

  const createQueue = (): NonNullable<Window['oaiq']> => {
    const queue = ((...args: unknown[]) => {
      queue.q = queue.q || [];
      queue.q.push(args);
    }) as NonNullable<Window['oaiq']>;

    return queue;
  };

  const queue = window.oaiq || createQueue();
  window.oaiq = queue;
  queue('init', { pixelId });

  appendAsyncScript({
    id: OPENAI_ADS_SCRIPT_ID,
    src: 'https://bzrcdn.openai.com/sdk/oaiq.min.js',
  });
};

const syncAdsConsent = (openAiPixelId?: string) => {
  updateMicrosoftAdsConsent();

  if (openAiPixelId && hasMarketingConsent()) {
    initOpenAiAds(openAiPixelId);
  }

  window.oaiq?.('consent', hasMarketingConsent());

  if (hasMarketingConsent()) {
    syncAdsUserData();
  } else {
    microsoftSyncedUserHash = undefined;
    openAiSyncedUserHash = undefined;
  }
};

const listenForCookiebotConsent = (openAiPixelId?: string) => {
  removeCookiebotConsentListeners?.();

  const handleConsentChange = () => syncAdsConsent(openAiPixelId);

  COOKIEBOT_CONSENT_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, handleConsentChange);
  });

  removeCookiebotConsentListeners = () => {
    COOKIEBOT_CONSENT_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, handleConsentChange);
    });
  };

  if (window.Cookiebot) handleConsentChange();
};

export const initAdsTracking = ({
  enabled = !IS_WHITE_LABEL,
  microsoftUetId = MICROSOFT_ADS_UET_ID,
  openAiPixelId = OPENAI_ADS_PIXEL_ID,
}: AdsTrackingConfig = {}) => {
  if (!enabled) {
    removeCookiebotConsentListeners?.();
    removeCookiebotConsentListeners = undefined;
    adsUserGeneration += 1;
    adsUserData = undefined;
    microsoftSyncedUserHash = undefined;
    openAiSyncedUserHash = undefined;
    return;
  }

  if (microsoftUetId) initMicrosoftAds(microsoftUetId);
  listenForCookiebotConsent(openAiPixelId);
};

const wasTrackedInSession = (key: string) => {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const markTrackedInSession = (key: string) => {
  try {
    sessionStorage.setItem(key, '1');
    return true;
  } catch {
    return false;
  }
};

const trackOncePerSession = (
  provider: 'microsoft' | 'openai',
  eventName: string,
  eventId: string,
  track: () => boolean,
) => {
  const key = `${ADS_SESSION_EVENT_PREFIX}:${provider}:${eventName}:${eventId}`;

  if (wasTrackedInSession(key)) return;
  if (track()) markTrackedInSession(key);
};

const trackMicrosoftAdsEvent = (eventName: string, properties: Record<string, unknown> = {}) => {
  if (!window.uetq) return false;

  window.uetq.push('event', eventName, properties);

  return true;
};

const trackOpenAiAdsEvent = (eventName: AdsEventName, data: Record<string, unknown> = {}, eventId?: string) => {
  if (!hasMarketingConsent() || !window.oaiq) return false;

  const openAiEvent = OPENAI_ADS_EVENTS[eventName];
  const payload = { type: openAiEvent.type, ...data };

  if (eventId) {
    window.oaiq('measure', openAiEvent.eventName, payload, { event_id: eventId });
    return true;
  }

  window.oaiq('measure', openAiEvent.eventName, payload);

  return true;
};

const trackConversion = ({
  deduplicateInSession = false,
  eventId,
  eventName,
  openAiData,
  revenue,
}: {
  deduplicateInSession?: boolean;
  eventId?: string;
  eventName: AdsEventName;
  openAiData?: Record<string, unknown>;
  revenue?: RevenueData;
}) => {
  const microsoftProperties = {
    event_category: 'conversion',
    ...(eventId && { event_label: eventId }),
    ...(revenue && {
      currency: revenue.currency,
      revenue_value: revenue.value,
    }),
  };
  const openAiPayload = {
    ...openAiData,
    ...(revenue && {
      amount: Math.round(revenue.value * 100),
      currency: revenue.currency,
    }),
  };

  if (deduplicateInSession && eventId) {
    trackOncePerSession('microsoft', eventName, eventId, () => trackMicrosoftAdsEvent(eventName, microsoftProperties));
    trackOncePerSession('openai', OPENAI_ADS_EVENTS[eventName].eventName, eventId, () =>
      trackOpenAiAdsEvent(eventName, openAiPayload, eventId),
    );
    return;
  }

  trackMicrosoftAdsEvent(eventName, microsoftProperties);
  trackOpenAiAdsEvent(eventName, openAiPayload, eventId);
};

const sha256 = async (value: string) => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));

  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};

const normalizeMicrosoftEmail = (email: string) => {
  const normalized = email
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s/g, '')
    .toLowerCase();
  const atIndex = normalized.lastIndexOf('@');

  if (atIndex <= 0) return;

  const domain = normalized.slice(atIndex + 1).replace(/\.+$/g, '');

  if (!domain.includes('.') || domain.startsWith('.')) return;

  const localPart = normalized.slice(0, atIndex).split('+', 1)[0]?.replace(/\./g, '');

  if (!localPart) return;

  return `${localPart}@${domain}`;
};

const identifyUser = async ({ id, country, email }: Pick<User, 'country' | 'email' | 'id'>) => {
  const generation = ++adsUserGeneration;
  const openAiEmail = email.trim().toLowerCase();
  const microsoftEmail = normalizeMicrosoftEmail(email);

  if (!openAiEmail || !microsoftEmail) return;

  try {
    const [emailSha256, externalIdSha256, microsoftEmailSha256] = await Promise.all([
      sha256(openAiEmail),
      sha256(id),
      sha256(microsoftEmail),
    ]);

    if (generation !== adsUserGeneration) return;

    const normalizedCountry = country?.trim().toUpperCase();

    adsUserData = {
      microsoftEmailSha256,
      openAiUser: {
        email_sha256: emailSha256,
        external_id_sha256: externalIdSha256,
        ...(normalizedCountry && /^[A-Z]{2}$/.test(normalizedCountry) && { country: normalizedCountry }),
      },
    };
    microsoftSyncedUserHash = undefined;
    openAiSyncedUserHash = undefined;
    syncAdsUserData();
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        feature: 'adsTracking',
        phase: 'enhancedMatchingHash',
      },
    });
  }
};

export const AdsEvents = {
  beginCheckout: ({ ecommerce }: BeginCheckoutEventData) => {
    trackConversion({
      eventName: ADS_EVENT_NAMES.beginCheckout,
      openAiData: {
        contents: ecommerce.items.map(({ item_id, item_name, quantity }) => ({
          id: item_id,
          name: item_name,
          content_type: 'product',
          quantity,
        })),
      },
      revenue: {
        currency: ecommerce.currency,
        value: ecommerce.value,
      },
    });
  },

  identifyUser,

  purchase: ({ currency, items, transaction_id, value }: IGetLatestPaymentInfoResponse) => {
    trackConversion({
      eventId: transaction_id,
      eventName: ADS_EVENT_NAMES.purchase,
      openAiData: {
        ...(items[0]?.item_id && { plan_id: items[0].item_id }),
      },
      revenue: { currency, value },
    });
  },

  signUp: (userId?: User['id']) => {
    trackConversion({
      eventId: userId,
      eventName: ADS_EVENT_NAMES.signUp,
    });
  },

  trialPreviewViewed: (projectId?: string) => {
    trackConversion({
      deduplicateInSession: true,
      eventId: projectId,
      eventName: ADS_EVENT_NAMES.trialPreviewViewed,
      openAiData: {
        ...(projectId && {
          contents: [
            {
              id: projectId,
              content_type: 'project',
            },
          ],
        }),
      },
    });
  },
};
